import { useCallback, useEffect, useId, useState } from "react";
import { StateMessage } from "@/components/StateMessage";
import { demoApi, problemMessage } from "@/api/client";
import type { RegisterCheck } from "@/api/types";
import { formatMoney } from "@/lib/format";
import "./PayRunPrint.css";

export interface RegisterCheckPrintProps {
  entryLineId: string;
  /** Same `navigate` prop shape as `PayRunPrint` - used for "Back to register". */
  onNavigate: (path: string) => void;
}

type Status = "loading" | "ready" | "error";

/**
 * `/books/checks/{entryLineId}` - ef-062-5. The printable view of one register check, OUTSIDE the app shell
 * exactly like `PayRunPrint`, and reusing its stylesheet and DOM shape (`PayRunPrint.css`) rather than copying it.
 *
 * A READ: visiting this URL never prints and never allocates a number (the check is printed from the register,
 * ef-062-2). This page hosts the Void action; a void keeps the number on record.
 */
export function RegisterCheckPrint({ entryLineId, onNavigate }: RegisterCheckPrintProps) {
  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState<string | null>(null);
  const [check, setCheck] = useState<RegisterCheck | null>(null);

  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [voidBusy, setVoidBusy] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  const reasonId = useId();

  useEffect(() => {
    const controller = new AbortController();

    async function load(): Promise<void> {
      setStatus("loading");
      setError(null);
      try {
        const loaded = await demoApi.getRegisterCheck(entryLineId, controller.signal);
        if (controller.signal.aborted) return;
        setCheck(loaded);
        setStatus("ready");
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(problemMessage(err, "Could not load this check."));
        setStatus("error");
      }
    }

    void load();
    return () => controller.abort();
  }, [entryLineId]);

  const backToRegister = useCallback(() => onNavigate("/books"), [onNavigate]);

  async function handleVoidConfirm(): Promise<void> {
    setVoidError(null);
    setVoidBusy(true);
    try {
      await demoApi.voidRegisterCheck(entryLineId, voidReason);
      setCheck(await demoApi.getRegisterCheck(entryLineId));
      setVoiding(false);
      setVoidReason("");
    } catch (err) {
      setVoidError(problemMessage(err, "Could not void this check."));
    } finally {
      setVoidBusy(false);
    }
  }

  return (
    <main className="pay-run-print" data-state={status}>
      {status === "loading" && <StateMessage tone="loading" title="Loading check…" />}

      {status === "error" && (
        <StateMessage
          tone="error"
          title="Could not load this check."
          detail={error ?? undefined}
          action={
            <button type="button" className="btn btn--primary" onClick={backToRegister}>
              Back to register
            </button>
          }
        />
      )}

      {status === "ready" && check && (
        <>
          <div className="pay-run-print__toolbar">
            <button type="button" className="btn btn--ghost" onClick={backToRegister}>
              Back to register
            </button>
            <button type="button" className="btn btn--primary" onClick={() => window.print()}>
              Print
            </button>
          </div>

          <section
            className="pay-run-print__check"
            aria-label={`Check ${check.checkNumber}`}
            data-state={check.voided ? "voided" : "ready"}
          >
            <header className="pay-run-print__check-header">
              <h1>Check #{check.checkNumber}</h1>
              <span className="pay-run-print__date">{formatCheckDate(check.occurredOn)}</span>
            </header>

            {check.voided && (
              <p className="pay-run-print__voided-badge" role="status">
                Voided{check.voidReason ? ` — ${check.voidReason}` : ""}
              </p>
            )}

            <dl className="pay-run-print__fields">
              <div className="pay-run-print__field">
                <dt>Pay to the order of</dt>
                <dd>{check.payeeName}</dd>
              </div>
              <div className="pay-run-print__field">
                <dt>Amount</dt>
                <dd className="pay-run-print__amount">{formatMoney(check.amount)}</dd>
              </div>
            </dl>

            <p className="pay-run-print__words">{check.amountInWords}</p>

            {check.memo && <p>{check.memo}</p>}
          </section>

          {!check.voided && (
            <section className="pay-run-print__void">
              {voidError && (
                <p className="pay-run-print__error" role="alert">
                  {voidError}
                </p>
              )}
              {!voiding ? (
                <button type="button" className="btn btn--danger" onClick={() => setVoiding(true)}>
                  Void this check
                </button>
              ) : (
                <div className="pay-run-print__void-form">
                  <label htmlFor={reasonId}>Reason</label>
                  <input
                    id={reasonId}
                    type="text"
                    value={voidReason}
                    onChange={(e) => setVoidReason(e.target.value)}
                    disabled={voidBusy}
                  />
                  <div className="pay-run-print__void-actions">
                    <button
                      type="button"
                      className="btn btn--ghost"
                      disabled={voidBusy}
                      onClick={() => {
                        setVoiding(false);
                        setVoidReason("");
                        setVoidError(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn--danger"
                      disabled={voidBusy}
                      onClick={() => void handleVoidConfirm()}
                    >
                      {voidBusy ? "Voiding…" : "Void"}
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
        </>
      )}
    </main>
  );
}

/** "2026-10-03" -> "Oct 3, 2026": the entry's calendar date, spelled the way the payroll check spells its dates. */
function formatCheckDate(iso: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${iso}T12:00:00Z`));
}
